import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, authApi, fetchCsrfCookie } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, passwordConfirmation: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // C01 — Sanctum SPA : on tente /me. Si la session est valide → user hydraté.
    // Si 401, l'intercepteur ignore (déjà sur /login ou route publique), on reste user=null.
    const initAuth = async () => {
      try {
        const response = await authApi.me();
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await fetchCsrfCookie();
      const response = await authApi.login({ email, password });
      setUser(response.data.user);
    } catch (error) {
      console.error('Erreur de connexion', error);
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string, passwordConfirmation: string) => {
    try {
      await fetchCsrfCookie();
      const response = await authApi.registerSimple({
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      setUser(response.data.user);
    } catch (error) {
      console.error('Erreur d\'inscription', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Erreur de déconnexion', error);
    } finally {
      setUser(null);
    }
  };

  const refreshUser = async (): Promise<User | null> => {
    try {
      const response = await authApi.me();
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement de l\'utilisateur', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
